import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true, timeout: 60000 });

// Auth is handled via an httpOnly session cookie set by the backend.
// This helper is kept for backward compatibility and returns no sensitive data.
export const adminHeaders = () => ({});

// Public
export const submitQuote = (data) => api.post("/quotes", data);
export const submitContact = (data) => api.post("/contact", data);
export const sendChat = (session_id, message) =>
  api.post("/ai/chat", { session_id, message });

// Admin
export const adminLogin = (password) => api.post("/admin/login", { password });
export const authLogin = (login, password) => api.post("/auth/login", { login, password });
export const authMe = () => api.get("/auth/me", { headers: adminHeaders() });
export const authLogout = () => api.post("/auth/logout", {}, { headers: adminHeaders() });
export const trackVisit = (data) => api.post("/track", data);
export const getAnalytics = () => api.get("/analytics", { headers: adminHeaders() });
export const getAgenda = () => api.get("/agenda", { headers: adminHeaders() });
export const genSocial = (data) => api.post("/social/generate", data, { headers: adminHeaders() });
export const saveSocial = (data) => api.post("/social", data, { headers: adminHeaders() });
export const listSocial = () => api.get("/social", { headers: adminHeaders() });
export const deleteSocial = (id) => api.delete(`/social/${id}`, { headers: adminHeaders() });

// Team / users (master admin)
export const syncTeam = () => api.post("/team/sync");
export const getTeam = () => api.get("/team", { headers: adminHeaders() });
export const createTeamUser = (data) => api.post("/team", data, { headers: adminHeaders() });
export const testTeamKey = (id) => api.post(`/team/${id}/test`, {}, { headers: adminHeaders() });
export const deleteTeamUser = (id) => api.delete(`/team/${id}`, { headers: adminHeaders() });
export const getStats = () => api.get("/admin/stats", { headers: adminHeaders() });
export const getQuotes = () => api.get("/quotes", { headers: adminHeaders() });
export const updateQuote = (id, status) =>
  api.put(`/quotes/${id}`, { status }, { headers: adminHeaders() });
export const deleteQuote = (id) =>
  api.delete(`/quotes/${id}`, { headers: adminHeaders() });
export const getMessages = () => api.get("/contact", { headers: adminHeaders() });
export const deleteMessage = (id) =>
  api.delete(`/contact/${id}`, { headers: adminHeaders() });
export const getProspects = () => api.get("/prospects", { headers: adminHeaders() });
export const createProspect = (data) =>
  api.post("/prospects", data, { headers: adminHeaders() });
export const scoreProspect = (id) => api.post(`/prospects/${id}/score`);
export const scoreAllProspects = (onlyMissing = true) => api.post("/prospects/score-all", null, { params: { only_missing: onlyMissing } });
export const getScoreStatus = () => api.get("/prospects/score-status");
export const enrichProspect = (id) => api.post(`/prospects/${id}/enrich`);
export const enrichMissingProspects = (limit = 10) => api.post(`/prospects/enrich-missing`, null, { params: { limit } });
export const getEnrichStatus = () => api.get("/prospects/enrich-status");
export const updateProspect = (id, data) =>
  api.put(`/prospects/${id}`, data, { headers: adminHeaders() });
export const deleteProspect = (id) =>
  api.delete(`/prospects/${id}`, { headers: adminHeaders() });
export const syncProspectDolibarr = (id) =>
  api.post(`/prospects/${id}/sync-dolibarr`, {}, { headers: adminHeaders() });
export const getAppointments = () =>
  api.get("/appointments", { headers: adminHeaders() });
export const createAppointment = (data) =>
  api.post("/appointments", data, { headers: adminHeaders() });
export const rescheduleAppointment = (id, date) => api.put(`/appointments/${id}/reschedule`, { date });
export const rescheduleIntervention = (id, date) => api.put(`/interventions/${id}/reschedule`, { date });
export const deleteAppointment = (id) =>
  api.delete(`/appointments/${id}`, { headers: adminHeaders() });
export const sendEmail = (data) =>
  api.post("/emails/send", data, { headers: adminHeaders() });
export const runAgent = (data) =>
  api.post("/agent/prospect", data, { headers: adminHeaders() });

// Leads (AI web search)
export const generateLeads = (data) =>
  api.post("/leads/generate", data, { headers: adminHeaders() });
export const importLeads = (data) =>
  api.post("/leads/import", data, { headers: adminHeaders() });

// Campaigns
export const getCampaigns = () => api.get("/campaigns", { headers: adminHeaders() });
export const createCampaign = (data) =>
  api.post("/campaigns", data, { headers: adminHeaders() });
export const runCampaign = (id) =>
  api.post(`/campaigns/${id}/run`, {}, { headers: adminHeaders() });
export const generateCampaign = (data) =>
  api.post("/campaigns/generate", data, { headers: adminHeaders() });
export const deleteCampaign = (id) =>
  api.delete(`/campaigns/${id}`, { headers: adminHeaders() });
export const scheduleCampaign = (id, scheduled_at) =>
  api.put(`/campaigns/${id}/schedule`, { scheduled_at }, { headers: adminHeaders() });
export const clearData = (type) =>
  api.post("/maintenance/clear", { type }, { headers: adminHeaders() });

// Autopilot
export const getAutopilot = () => api.get("/autopilot/settings", { headers: adminHeaders() });
export const updateAutopilot = (data) => api.put("/autopilot/settings", data, { headers: adminHeaders() });
export const getAutopilotLogs = () => api.get("/autopilot/logs", { headers: adminHeaders() });
export const getGeoVilles = (params) => api.get("/geo/villes", { params, headers: adminHeaders() });
export const getGeoProspects = () => api.get("/geo/prospects", { headers: adminHeaders() });
export const getWeeklySeries = (weeks = 8) => api.get("/autopilot/weekly-series", { params: { weeks }, headers: adminHeaders() });
export const runAutopilotNow = () => api.post("/autopilot/run-now", {}, { headers: adminHeaders() });

// Email / SMTP settings
export const getEmailSettings = () => api.get("/settings/email", { headers: adminHeaders() });
export const updateEmailSettings = (data) => api.put("/settings/email", data, { headers: adminHeaders() });
export const testEmailSettings = (data) => api.post("/settings/email/test", data, { headers: adminHeaders() });
export const testImapSettings = (data) => api.post("/settings/email/test-imap", data, { headers: adminHeaders() });

// Boîte mail + agent IA
export const getMailbox = (folder = "inbox") => api.get("/mailbox", { params: { folder } });
export const syncMailbox = () => api.post("/mailbox/sync");
export const getMail = (id) => api.get(`/mailbox/${id}`);
export const deleteMail = (id) => api.delete(`/mailbox/${id}`);
export const sendMail = (data) => api.post("/mailbox/send", data);
export const analyzeMail = (id) => api.post(`/mailbox/${id}/analyze`);
export const scheduleFromMail = (id, data) => api.post(`/mailbox/${id}/schedule`, data);
export const confirmFromMail = (id) => api.post(`/mailbox/${id}/confirm`);
export const runRdvReminders = () => api.post("/mail-agent/rdv-reminders");
export const getMailAvailability = (days = 10) => api.get("/mailbox/availability", { params: { days } });
export const getMailAgentSettings = () => api.get("/settings/mail-agent");
export const updateMailAgentSettings = (data) => api.put("/settings/mail-agent", data);
export const runMailAgent = () => api.post("/mail-agent/run");

// Interventions & pointage GPS
export const fileUrl = (path) => `${API}/files/${path}`;
export const uploadFile = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/upload", fd, { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } });
};
export const getInterventions = () => api.get("/interventions", { headers: adminHeaders() });
export const getMyInterventions = () => api.get("/interventions/mine", { headers: adminHeaders() });
export const createIntervention = (data) => api.post("/interventions", data, { headers: adminHeaders() });
export const updateIntervention = (id, data) => api.put(`/interventions/${id}`, data, { headers: adminHeaders() });
export const deleteIntervention = (id) => api.delete(`/interventions/${id}`, { headers: adminHeaders() });
export const setInterventionStatus = (id, status) => api.put(`/interventions/${id}/status`, { status });
export const startIntervention = (id, data) => api.post(`/interventions/${id}/start`, data, { headers: adminHeaders() });
export const endIntervention = (id, data) => api.post(`/interventions/${id}/end`, data, { headers: adminHeaders() });
export const checkinIntervention = (id, data) => api.post(`/interventions/${id}/checkin`, data, { headers: adminHeaders() });
export const checkoutIntervention = (id, data) => api.post(`/interventions/${id}/checkout`, data, { headers: adminHeaders() });
export const getTracking = (token) => api.get(`/track/intervention/${token}`);
export const getGpsSettings = () => api.get("/settings/gps", { headers: adminHeaders() });
export const updateGpsSettings = (radius_m) => api.put("/settings/gps", { radius_m }, { headers: adminHeaders() });
export const geocodeIntervention = (id) => api.post(`/interventions/${id}/geocode`, {}, { headers: adminHeaders() });
export const setInterventionLocation = (id, lat, lng) => api.put(`/interventions/${id}/location`, { lat, lng }, { headers: adminHeaders() });
export const getReportsSettings = () => api.get("/settings/reports", { headers: adminHeaders() });
export const updateReportsSettings = (auto_send) => api.put("/settings/reports", { auto_send }, { headers: adminHeaders() });
export const generateReport = (id) => api.post(`/interventions/${id}/report`, {}, { headers: adminHeaders() });
export const sendReport = (id, to) => api.post(`/interventions/${id}/report/send`, { to, origin: window.location.origin }, { headers: adminHeaders() });
export const getReminderSettings = () => api.get("/settings/reminders", { headers: adminHeaders() });
export const updateReminderSettings = (enabled, hour) => api.put("/settings/reminders", { enabled, hour }, { headers: adminHeaders() });

// Clients récurrents & planification automatique
export const getContracts = () => api.get("/contracts");
export const createContract = (data) => api.post("/contracts", data);
export const updateContract = (id, data) => api.put(`/contracts/${id}`, data);
export const deleteContract = (id) => api.delete(`/contracts/${id}`);
export const previewPlan = (month) => api.get("/contracts/preview", { params: { month } });
export const generatePlan = (month, contract_ids = null, reports = null) => api.post("/contracts/generate", { month, contract_ids, reports });
export const getAutoPlan = () => api.get("/settings/auto-plan");
export const setAutoPlan = (data) => api.put("/settings/auto-plan", data);

// Jours fériés & charge par easer
export const getHolidays = (year) => api.get("/holidays", { params: { year } });
export const getWorkload = (week) => api.get("/team/workload", { params: { week } });
export const getWorkloadSettings = () => api.get("/settings/workload");
export const updateWorkloadSettings = (threshold_h) => api.put("/settings/workload", { threshold_h });

// Absences (congés Dolibarr + CRM) & remplacement d'easer
export const getAbsences = (params) => api.get("/absences", { params });
export const createAbsence = (data) => api.post("/absences", data);
export const deleteAbsence = (id) => api.delete(`/absences/${id}`);
export const syncAbsences = () => api.post("/absences/sync");
export const getCandidates = (id) => api.get(`/interventions/${id}/candidates`);
export const reassignIntervention = (id, data) => api.post(`/interventions/${id}/reassign`, data);
export const updateTeamProfile = (id, data) => api.put(`/team/${id}/profile`, data);
export const getReplacementSettings = () => api.get("/settings/replacement");
export const updateReplacementSettings = (radius_km) => api.put("/settings/replacement", { radius_km });
export const getMyAbsences = () => api.get("/absences/mine", { headers: adminHeaders() });
export const requestAbsence = (data) => api.post("/absences/request", data, { headers: adminHeaders() });
export const approveAbsence = (id) => api.post(`/absences/${id}/approve`);
export const rejectAbsence = (id) => api.post(`/absences/${id}/reject`);

// Recrutement (agent offres d'emploi) + candidatures
export const generateJob = (data) => api.post("/jobs/generate", data);
export const getJobs = () => api.get("/jobs");
export const createJob = (data) => api.post("/jobs", data);
export const updateJob = (id, data) => api.put(`/jobs/${id}`, data);
export const deleteJob = (id) => api.delete(`/jobs/${id}`);
export const publishJob = (id, channels, unpublish = false) => api.post(`/jobs/${id}/publish`, { channels, unpublish });
export const setJobFtStatus = (id, status, reference = "") => api.put(`/jobs/${id}/channel/france_travail`, { status, reference });
export const getCandidatures = () => api.get("/candidatures");
export const updateCandidature = (id, status) => api.put(`/candidatures/${id}`, { status });
export const deleteCandidature = (id) => api.delete(`/candidatures/${id}`);
export const getPublicJobs = () => api.get("/public/jobs");
export const getPublicJob = (id) => api.get(`/public/jobs/${id}`);
export const applyJob = (id, formData) => api.post(`/public/jobs/${id}/apply`, formData, { headers: { "Content-Type": "multipart/form-data" } });

// Tiers Dolibarr + contacts liés
export const getTiers = () => api.get("/tiers");
export const getTiersDetail = (id) => api.get(`/tiers/${id}`);
export const getTiersQuality = (id) => api.get(`/tiers/${id}/quality`);
export const archiveTiers = (id, archived, reason = "") => api.put(`/tiers/${id}/archive`, { archived, reason });
export const deleteTiers = (id) => api.delete(`/tiers/${id}`);
export const getTiersDuplicates = () => api.get("/tiers/duplicates");
export const checkTiersDuplicate = (email, name = "") => api.get("/tiers/check-duplicate", { params: { email, name } });
export const mergeTiers = (keepId, mergeIds) => api.post("/tiers/merge", { keep_id: keepId, merge_ids: mergeIds });
export const createTiers = (data) => api.post("/tiers", data);
export const createTiersContact = (socid, data) => api.post(`/tiers/${socid}/contacts`, data);
export const updateTiersContact = (id, data) => api.put(`/tiers/contacts/${id}`, data);
export const deleteTiersContact = (id) => api.delete(`/tiers/contacts/${id}`);

// Questionnaire services (fiche Tiers) & Contrôle qualité
export const getQualityGrid = () => api.get("/quality/grid");
export const getTiersQuestionnaire = (socid) => api.get(`/tiers/${socid}/questionnaire`);
export const saveTiersQuestionnaire = (socid, answers) => api.put(`/tiers/${socid}/questionnaire`, { answers });
export const getQualitySites = () => api.get("/quality/sites");
export const getQualityControls = (params = {}) => api.get("/quality/controls", { params });
export const createQualityControl = (data) => api.post("/quality/controls", data);
export const sendQualityReport = (id) => api.post(`/quality/controls/${id}/send`);
export const deleteQualityControl = (id) => api.delete(`/quality/controls/${id}`);
export const qualityPdfUrl = (id) => `${API}/quality/controls/${id}/pdf`;
export const getQualityActions = (params = {}) => api.get("/quality/actions", { params });
export const updateQualityAction = (controlId, actionId, data) => api.put(`/quality/controls/${controlId}/actions/${actionId}`, data);
export const sendQualityReminders = () => api.post("/quality/reminders/send");
export const getQualityReminderStatus = () => api.get("/quality/reminders/status");

// Tickets clients (public)
export const getTicketTypes = () => api.get("/public/tickets/types");
export const lookupTicketEmail = (email) => api.get("/public/tickets/lookup", { params: { email } });
export const createPublicTicket = (data) => api.post("/public/tickets", data);
export const getPublicTicket = (ref, code) => api.get(`/public/tickets/${encodeURIComponent(ref)}`, { params: { code } });
export const replyPublicTicket = (ref, code, message) => api.post(`/public/tickets/${encodeURIComponent(ref)}/messages`, { message }, { params: { code } });

// Synchronisation Dolibarr (audit des envois)
export const getDolibarrSyncReport = () => api.get("/dolibarr/sync-report");
export const retryDolibarrSync = () => api.post("/dolibarr/sync-retry");
export const updateDolibarrSyncSettings = (data) => api.put("/settings/dolibarr-sync", data);

// Pages services (site) & galerie réalisations
export const getPublicServices = () => api.get("/public/services");
export const getPublicService = (slug) => api.get(`/public/services/${slug}`);
export const getPublicGallery = () => api.get("/public/gallery");
export const getServicePages = () => api.get("/service-pages", { headers: adminHeaders() });
export const updateServicePage = (slug, data) => api.put(`/service-pages/${slug}`, data, { headers: adminHeaders() });
export const regenerateServicePage = (slug, brief) => api.post(`/service-pages/${slug}/regenerate`, { brief }, { headers: adminHeaders() });
export const setInterventionGallery = (id, data) => api.put(`/interventions/${id}/gallery`, data, { headers: adminHeaders() });
export const getGallerySettings = () => api.get("/settings/gallery", { headers: adminHeaders() });
export const updateGallerySettings = (show_demo) => api.put("/settings/gallery", { show_demo }, { headers: adminHeaders() });
export const getPublicLegal = () => api.get("/public/legal");
export const getLegalSettings = () => api.get("/settings/legal", { headers: adminHeaders() });
export const updateLegalSettings = (data) => api.put("/settings/legal", data, { headers: adminHeaders() });
export const getSimulatorRates = () => api.get("/public/simulator-rates");
export const getSimulatorSettings = () => api.get("/settings/simulator", { headers: adminHeaders() });
export const updateSimulatorSettings = (data) => api.put("/settings/simulator", data, { headers: adminHeaders() });
